import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Square, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [email, setEmail] = useState("demo@demo.com");
  const [password, setPassword] = useState("demo123");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      const result = login({ email, password });
      setLoading(false);
      if (result.ok) {
        toast.success("Welcome back!");
        navigate("/dashboard", { replace: true });
      } else {
        toast.error(result.error);
      }
    }, 350);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div className="absolute top-[-20%] right-[-10%] h-[500px] w-[500px] rounded-full bg-primary/20 dark:bg-primary/25/40 blur-3xl" />
      <div className="absolute bottom-[-20%] left-[-10%] h-[500px] w-[500px] rounded-full bg-slate-200 dark:bg-slate-700/40 blur-3xl" />

      <div className="relative w-full max-w-md px-6">
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 elev-3 p-8 animate-scale-in">
          <div className="flex items-center gap-2.5 mb-8">
            <div
              className={`h-9 w-9 rounded-lg flex items-center justify-center overflow-hidden float-subtle ${
                tenant.logo ? "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700" : "bg-primary"
              }`}
              data-testid="login-brand-tile"
            >
              {tenant.logo ? (
                <img
                  src={tenant.logo}
                  alt={tenant.workspaceName}
                  className="h-full w-full object-contain"
                  data-testid="login-brand-logo"
                />
              ) : (
                <Square className="h-4 w-4 text-white" fill="white" />
              )}
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-display text-sm font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid="login-workspace-name">
                {tenant.workspaceName.replace(/\s*Portal$/i, "")}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Portal
              </div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Sign in to access your command center.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="demo@demo.com"
                data-testid="login-email-input"
                className="h-11 rounded-lg"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="demo123"
                data-testid="login-password-input"
                className="h-11 rounded-lg"
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium group btn-premium"
            >
              {loading ? "Signing in..." : "Sign In"}
              <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          <div className="mt-6 rounded-lg border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 dark:bg-slate-900/50 px-4 py-3">
            <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.18em]">
              Demo credentials
            </div>
            <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 font-mono">
              demo@demo.com · demo123
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-6">
          A cloneable portal template — built for any business.
        </p>
      </div>
    </div>
  );
}
